/**
 * fireflies — sparse drifting glow motes that breathe (alpha yoyo) + drift
 * (positional yoyo) on async sine cycles. Migrated 1:1 from
 * AtmosphereLayer.buildFireflies into a registry builder.
 *
 * Reactivity (world-combat spec §2.4): the handle opts into the positional
 * pulse channel — motes inside a pulse radius SCATTER away from the impact
 * point (transient kick, falloff by distance), then ease back to their
 * authored spawn + resume the idle breathing/drift tweens. Transient only —
 * no authored value is ever changed. Reduce-motion builds without the hook.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { placeFireflies } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import type { ISprite } from '../../../rendering/IRenderer';

/** Scatter kick tuning — outward hop then a slow ease home (~1.2s total). */
const SCATTER_OUT_MS = 300;
const SCATTER_HOME_MS = 900;
const SCATTER_MIN_PX = 40;
const SCATTER_MAX_EXTRA_PX = 140;

interface Mote {
  sprite: ISprite;
  /** Authored placement (home anchor + drift/breathing params). */
  f: ReturnType<typeof placeFireflies>[number];
  /** True while a scatter round-trip owns the sprite's tweens. */
  scattering: boolean;
}

/** (Re)attach the authored idle tweens — breathing + positional drift. */
function attachIdleTweens(s: ISprite, f: Mote['f']): void {
  const half = f.periodMs / 2;
  // Alpha waver (breathing) — async per mote via the phase-shifted period.
  s.tween({ alpha: f.alphaMax }, { durationMs: half, ease: 'sineInOut', yoyo: true, repeat: -1 });
  // Slow positional drift around the spawn point.
  s.tween({ x: f.x + f.driftX, y: f.y + f.driftY }, { durationMs: f.periodMs, ease: 'sineInOut', yoyo: true, repeat: -1 });
}

const buildFireflies: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'fireflies') return null;
  const reduce = getMotionPrefs().reduceMotion; // read ONCE at build time
  const motes: Mote[] = [];
  for (const f of placeFireflies(layer, ctx.region, ctx.seed)) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const s = createGlowSprite({ x: f.x, y: f.y, size: f.size, color: f.color, alpha: f.alphaMin, textureKey: layer.textureKey, blendMode: layer.blendMode, parent: ctx.layerContainer });
    if (!s) break;
    motes.push({ sprite: s, f, scattering: false });
    // Reduced motion: each mote holds its spawn point, midway through its breathe.
    if (reduce) s.setAlpha((f.alphaMin + f.alphaMax) / 2);
    else attachIdleTweens(s, f);
  }

  // Positional pulse: motes inside `radius` hop away from (px, py) with
  // distance falloff, then tween home + resume idle tweens. Skipped entirely
  // under reduce-motion (the driver also holds calm there — double safety).
  const onPulse = reduce ? undefined : (px: number, py: number, intensity: number, radius: number) => {
    const kick = Math.max(0, Math.min(1, intensity));
    for (const m of motes) {
      if (m.scattering) continue; // one scatter at a time per mote
      const dx = m.sprite.x - px;
      const dy = m.sprite.y - py;
      const dist = Math.hypot(dx, dy);
      if (dist > radius) continue;
      const falloff = 1 - dist / radius;
      const mag = (SCATTER_MIN_PX + SCATTER_MAX_EXTRA_PX * kick) * falloff;
      // Direction away from the pulse; a mote AT the point picks a stable
      // per-mote angle from its authored phase (no allocation, no RNG churn).
      const ux = dist > 1e-3 ? dx / dist : Math.cos(m.f.phase * Math.PI * 2);
      const uy = dist > 1e-3 ? dy / dist : Math.sin(m.f.phase * Math.PI * 2);
      m.scattering = true;
      m.sprite.killAllTweens();
      m.sprite.tween({ x: m.sprite.x + ux * mag, y: m.sprite.y + uy * mag }, {
        durationMs: SCATTER_OUT_MS,
        ease: 'sineOut',
        onComplete: () => {
          m.sprite.tween({ x: m.f.x, y: m.f.y }, {
            durationMs: SCATTER_HOME_MS,
            ease: 'sineInOut',
            onComplete: () => {
              m.scattering = false;
              attachIdleTweens(m.sprite, m.f); // resume the authored behavior
            },
          });
        },
      });
    }
  };

  return makeContentHandle({ sprites: motes.map((m) => m.sprite), onPulse });
};

registerEffect({ kind: 'fireflies', build: buildFireflies, depth: Z_LAYERS.ATMOSPHERE });
